import React, { useContext, useEffect, useState } from 'react'
import { Page } from './Page'
import { Title } from '../components/common/texts/Title'
import { useMsal } from '@azure/msal-react'
import { Divider, makeStyles, tokens } from '@fluentui/react-components'
import { Grid } from '../components/common/grid/Grid'
import { GridItem } from '../components/common/grid/GridItem'
import { BasicCard } from '../components/common/card/BasicCard'
import { Activity, LawImportStatus } from '../api/schemas/schema'
import { Loader } from '../components/common/spinner/Loader'
import { ErrorFallback } from '../components/common/error/ErrorFallback'
import { DashboardSidebar } from './Dashboard/DashboardSidebar'
import { List } from '../components/common/list/List'
import { getActivitiesColumns } from '../helpers/activitiesHelper'
import { DataContext } from '../App'
import { t } from '../i18n/i18n'
import { api } from '../helpers/apiHelper'

const useStyles = makeStyles({
   title: {
      fontSize: tokens.fontSizeHero800,
   },
   noMargin: {
      margin: 0,
   },
   alignBottom: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
   },
})
export type DashboardState = {
   numberOfQuestions: number
   numberOfCustomers: number | null
   latestActivities: Activity[]
   lawImportStats: LawImportStatus
}
export const Dashboard = () => {
   const dataContext = useContext(DataContext)
   const { accounts } = useMsal()
   const classes = useStyles()
   const [dashboardState, setDashboardState] = useState<DashboardState>()

   const { state, setRootState } = useContext(DataContext)
   const { accessToken } = state
   const [isLoading, setIsLoading] = useState<boolean>(true)
   const [isCustomersLoading, setIsCustomersLoading] = useState<boolean>(true)
   const [loadError, setLoadError] = useState<unknown>(null)

   useEffect(() => {
      if (state.accessToken) {
         setRootState((prev) => ({ ...prev, isDashboard: true }))
         const controller = new AbortController()
         const { signal } = controller

         ;(async () => {
            try {
               const [numberOfQuestions, lawImportStatus, activities] = await Promise.all([
                  api(accessToken).questionCountList({ signal }),
                  api(accessToken).lawImportStatusList({ signal }),
                  api(accessToken).activityList({ take: 14 }, { signal }),
               ])
               setDashboardState((prev) => ({
                  ...prev,
                  numberOfQuestions: numberOfQuestions.data,
                  lawImportStats: lawImportStatus.data,
                  latestActivities: activities.data,
               }))
            } catch (error: any) {
               if (error?.name !== 'AbortError') {
                  setLoadError(error)
                  dataContext.handleMessage(dataContext.setRootState, 'error', t('SomethingWentWrong'), t('CouldntFetchDashboardData'))
               }
            } finally {
               if (!signal.aborted) setIsLoading(false)
            }
         })()

         ;(async () => {
            try {
               const result = await api(accessToken).countCustomers({ signal })
               setDashboardState((prev) => ({ ...prev, numberOfCustomers: result.data }))
            } catch {
               // customer API offline or aborted — card shows '-'
            } finally {
               if (!signal.aborted) setIsCustomersLoading(false)
            }
         })()

         return () => {
            setRootState((prev) => ({ ...prev, isDashboard: false }))
            controller.abort()
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.accessToken])

   return (
      <Page header={<Title as="h2">{t('Dashboard')}</Title>}>
         {loadError ? (
            <ErrorFallback error={loadError} />
         ) : isLoading || !dashboardState ? (
            <Loader />
         ) : (
            <Grid>
               <GridItem size="9/12" className={classes.alignBottom}>
                  <Title as="h3" className={classes.title}>
                     {t('Welcome')} {accounts[0].name.split(' ')[0]}!
                  </Title>
                  <BasicCard title={t('OngoingActivities')} color="beige" titleClassName={classes.noMargin}>
                     <Divider appearance="brand" />
                     <List
                        items={dashboardState?.latestActivities}
                        columns={getActivitiesColumns()}
                        listClassName={classes.noMargin}
                        resizableColumns
                        columnSizingOptions={{
                           lastModified: {
                              minWidth: 120,
                              defaultWidth: 150,
                           },
                           modifiedBy: {
                              defaultWidth: 220,
                              minWidth: 150,
                           },
                        }}
                     />
                  </BasicCard>
               </GridItem>
               <DashboardSidebar dashboardState={dashboardState} isCustomersLoading={isCustomersLoading} />
            </Grid>
         )}
      </Page>
   )
}
